import '../styles/index.scss';

import { getToken, TOKEN_KEY } from '../config/api';
import getAxiosInstance from '../config/api';
import { useEffect } from 'react';
import withReduxStore from '../shared/with-redux-store';
import { Provider } from 'react-redux';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Sidebar from '../components/Sidebar';
import Body from '../components/Body';
import ServerCookies from 'cookies';
import Cookies from 'js-cookie';
import { getProfile } from '../shared/actions/auth.actions';
import _ from 'lodash';
import {
  setListStore,
  fetchMoreAndReturnNewState,
} from '../shared/actions/generated/alert.actions';
import Script from 'next/script';
import ErrorBoundary from 'flaivy-react/components/ErrorBoundary';
import { MountPoint } from 'flaivy-react/components/Confirmation';

import * as Sentry from '@sentry/nextjs';

const SentryErrorBoundary = Sentry.ErrorBoundary;

const isLocalhost = req => {
  const host = req?.headers?.host
    ? req.headers.host
    : typeof window !== 'undefined' && window.location.hostname;

  return _.includes(host, 'localhost') || _.includes(host, '127.0.0.1');
};

const App = props => {
  const {
    Component,
    pageProps,
    reduxStore,
    router: { pathname },
  } = props;
  const googleAnalyticsKey = `UA-78281181-3`;
  const isAppPage = pathname && pathname.match(/\/app.*/);
  const isLoginPage = pathname && pathname.match(/\/login.*/);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      if (typeof window !== 'undefined') {
        import('../sentry.client.config');
      }

      const sessionUser = reduxStore.getState().authentication?.sessionUser;

      if (sessionUser && sessionUser.id) {
        Sentry.setUser({
          id: sessionUser.id,
          email: sessionUser.email,
          username: sessionUser.name,
        });
        Sentry.setContext('sessionUser', {
          id: sessionUser.id,
          name: sessionUser.name,
          email: sessionUser.email,
          customerNumber: sessionUser.customerNumber,
        });
      } else {
        Sentry.setUser(null);
        Sentry.setContext('sessionUser', null);
      }
    }
  }, [reduxStore]);

  if (isLoginPage) {
    return (
      <Provider store={reduxStore}>
        <ErrorBoundary>
          <MountPoint></MountPoint>
          <SentryErrorBoundary
            fallback={({ error, resetError }) => (
              <div>
                <h1>An error occurred on the client!</h1>
                <pre style={{ whiteSpace: 'pre-wrap' }}>
                  {error?.toString()}
                </pre>
                <button onClick={resetError}>Try again</button>
              </div>
            )}
            showDialog
            beforeCapture={scope => {
              scope.setTag(
                'component',
                Component.displayName || Component.name || 'unknown',
              );
              return scope;
            }}
          >
            <Component {...pageProps} />;
          </SentryErrorBoundary>
        </ErrorBoundary>
        <Script
          src="https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID"
          strategy="afterInteractive"
        />
        <Script id="google-analytics" strategy="afterInteractive">
          {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', '${googleAnalyticsKey}');
        `}
        </Script>
      </Provider>
    );
  }
  if (isAppPage) {
    return (
      <Provider store={reduxStore}>
        <Body>
          <div className="wrapper">
            <Sidebar />
            <div className="content-page">
              <div className="content">
                <Header />
                <div className="row pt-2">
                  <div className="col-12">
                    <SentryErrorBoundary
                      fallback={({ error, resetError }) => (
                        <div>
                          <h1>An error occurred on the client!</h1>
                          <pre style={{ whiteSpace: 'pre-wrap' }}>
                            {error?.toString()}
                          </pre>
                          <button onClick={resetError}>Try again</button>
                        </div>
                      )}
                      showDialog
                      beforeCapture={scope => {
                        scope.setTag(
                          'component',
                          Component.displayName || Component.name || 'unknown',
                        );
                        return scope;
                      }}
                    >
                      <MountPoint></MountPoint>
                      <Component {...pageProps} />
                    </SentryErrorBoundary>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </Body>
        <Script
          src="https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID"
          strategy="afterInteractive"
        />
        <Script id="google-analytics" strategy="afterInteractive">
          {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', '${googleAnalyticsKey}');
        `}
        </Script>
        <Script
          type="text/javascript"
          id="claitycode"
          strategy="afterInteractive"
        >
          {`
    (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "p40zaztsb1");        `}
        </Script>
      </Provider>
    );
  }

  return (
    <>
      <Provider store={reduxStore}>
        <Component {...pageProps} />
        <Script
          src="https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID"
          strategy="afterInteractive"
        />
        <Script id="google-analytics" strategy="afterInteractive">
          {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', '${googleAnalyticsKey}');
        `}
        </Script>
        <Script
          type="text/javascript"
          id="claitycode"
          strategy="afterInteractive"
        >
          {`
    (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "p40zaztsb1");        `}
        </Script>
      </Provider>
    </>
  );
};

async function redirectToLogin({ res, req }) {
  const targetURL = '/login';
  if (res) {
    // On the server,

    if (!req.url.match(/login.*/) && req.url !== '/') {
      const cookies = new ServerCookies(req, res);
      cookies.set('redirect_after_login', req.url, {
        httpOnly: false, // true by default
      });
    }
    // we'll use an HTTP response to
    // redirect with the status code of our choice.
    // 307 is for temporary redirects.
    res.writeHead(307, { Location: targetURL });
    res.end();
  } else {
    // We'll redirect to the external page using
    // `window.location`.
    window.location = targetURL;
    // While the page is loading, code execution will
    // continue, so we'll await a never-resolving
    // promise to make sure our page never
    // gets rendered.
    await new Promise(() => {});
  }
  return {};
}

async function redirectToMaintenance({ res, req }) {
  const targetURL = '/maintenance';
  if (isLocalhost(req)) {
    return;
  }
  if (typeof window !== 'undefined' && window.location) {
    // We'll redirect to the external page using
    // `window.location`.
    window.location = targetURL;
    // While the page is loading, code execution will
    // continue, so we'll await a never-resolving
    // promise to make sure our page never
    // gets rendered.
    await new Promise(() => {});
  } else {
    // On the server,
    if (!req.url.match(/maintenance.*/)) {
      const cookies = new ServerCookies(req, res);
      cookies.set('redirect_after_login', req.url, {
        httpOnly: false, // true by default
      });
    }
    // we'll use an HTTP response to
    // redirect with the status code of our choice.
    // 307 is for temporary redirects.
    res.writeHead(307, { Location: targetURL });
    res.end();
  }
  return {};
}

async function checkIfWereUp(req) {
  const instance = getAxiosInstance(req);
  try {
    const response = await instance.get('/public/health');
    return response.status === 200;
  } catch (e) {
    console.error(e);
    return false;
  }
}

App.getInitialProps = async ({ Component, ctx }) => {
  const { req, pathname, res } = ctx;
  // Sentry user context for SSR errors
  const authentication = ctx.reduxStore.getState().authentication;
  const serverSessionUser = authentication?.sessionUser;
  if (serverSessionUser && serverSessionUser.id) {
    Sentry.setUser({
      id: serverSessionUser.id,
      email: serverSessionUser.email,
      username: serverSessionUser.name,
    });
  } else {
    Sentry.setUser(null);
  }

  let host;

  if (req) {
    // Server side rendering
    // eslint-disable-next-line
    host = req.headers.host;
  } else {
    host = window.location.hostname;
  }

  const isAppPage = pathname && pathname.match(/\/app.*/);
  const isLoginPage = pathname && pathname.match(/\/login.*/);
  const isPublicPage = pathname && pathname.match(/\/public.*/);
  const isFirstPage = pathname === '/';

  if (!isAppPage && !isLoginPage && !isPublicPage && !isFirstPage) {
    return;
  }

  const hasJWTToken = getToken(req);
  if (!hasJWTToken && !isLoginPage && !isPublicPage) {
    await redirectToLogin(ctx);
  }
  const weAreUp = await checkIfWereUp(req);
  if (!weAreUp) {
    await redirectToMaintenance(ctx);
    return;
  }
  const auth = ctx.reduxStore.getState().authentication;
  if (hasJWTToken && !_.get(auth, 'isAuthenticated') && !isPublicPage) {
    try {
      await ctx.reduxStore.dispatch(getProfile(req));
      const alertList = await fetchMoreAndReturnNewState(
        { isClosed: false },
        { sort: 'color', currentPage: 0 },
        req,
      );
      await ctx.reduxStore.dispatch(setListStore(alertList));
    } catch (e) {
      if (req) {
        const cookies = new ServerCookies(req, res);
        cookies.set(TOKEN_KEY);
      } else {
        Cookies.remove(TOKEN_KEY);
      }
      await redirectToLogin(ctx);
    }
  }

  let pageProps = {};

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
  }
  return {
    pageProps,
    pathname,
    isDevCluster: _.includes(host, 'dev'),
  };
};

export default withReduxStore(App);